<template>
  <div
    class="c-virtual-list__row keyword"
    :class="{ 'is-selected': isSelected }"
    ref="listItemKeyword"
  >
    <div class="c-virtual-list__col check" @click.prevent="click">
      <Checkbox
        :id="'keyword_' + item.id"
        name="keywords[]"
        :value="item.id"
        :checked="checked"
        ref="checkbox"
        @change="change"
      />
    </div>
    <div class="c-virtual-list__col keyword" @click="select">
      <div class="inner">
        <EllipsisPopover placement="right-end">
          {{ item.keyword }}
        </EllipsisPopover>
        <Loading v-if="item.isLoading" variant="inline" />
        <Popover
          v-else-if="isError"
          placement="right-end"
          size="sm"
          variant="attention"
          @click.native="goToSupport"
        >
          <Icon2 variant="attention" :size="16" />
          <template slot="popover">
            データが取得できません<br />（原因は
            <a
              href="https://support.searchwrite.jp/hc/ja/articles/360004427875-Google%E3%82%A2%E3%82%AB%E3%82%A6%E3%83%B3%E3%83%88%E3%81%A8%E3%81%AE%E9%80%A3%E6%90%BA%E3%81%8C%E5%88%87%E3%82%8C%E3%81%A6%E3%81%97%E3%81%BE%E3%81%A3%E3%81%9F"
              target="_blank"
              class="c-link--blank"
              >こちら</a
            >）
          </template>
        </Popover>
      </div>
    </div>
    <div class="c-virtual-list__col url">
      <EllipsisPopover :clamp="2" placement="right-end">
        <a :href="item.url" target="_blank" class="c-link--blank" @click.stop>
          {{ item.url | removeDomain(project.siteUrl) }}
        </a>
        <template slot="popover">
          <a :href="item.url" target="_blank" @click.stop>
            {{ item.url | removeDomain(project.siteUrl) }}
          </a>
        </template>
      </EllipsisPopover>
    </div>
    <div class="c-virtual-list__col task">
      <div v-if="hasDoneTask" class="c-list--category">
        <p :class="categoryCssClass">
          {{ category }}
        </p>
        <p class="c-list--date">
          {{ item.taskDoneDate | dateText('YYYY.MM.DD') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { CALENDAR } from '@/constants';
import Checkbox from '@/components/Checkbox';
import EllipsisPopover from '@/components/EllipsisPopover';
import Icon2 from '@/components/Icon2';
import Loading from '@/components/Loading';
import Popover from '@/components/Popover';
import utils from '@/utils';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    dates: {
      type: Object,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    selectedId: {
      type: String,
      required: true
    }
  },
  components: {
    Checkbox,
    EllipsisPopover,
    Icon2,
    Loading,
    Popover
  },
  data() {
    return {
      isAlreadyDisplayed: false,
      offsetH: 0,
      scrollH: 0
    };
  },
  mounted() {
    const listItem = this.$refs.listItemKeyword;
    const handler = entries => {
      if (entries[0].isIntersecting) {
        this.isAlreadyDisplayed = true;
      }
    };
    const observer = new window.IntersectionObserver(handler);
    observer.observe(listItem);
  },
  updated() {
    const listItem = this.$refs.listItemKeyword;
    const handler = entries => {
      if (entries[0].isIntersecting) {
        this.isAlreadyDisplayed = true;
      }
    };
    const observer = new window.IntersectionObserver(handler);
    observer.observe(listItem);
  },
  watch: {
    isAlreadyDisplayed: {
      handler(value) {
        if (value) {
          this.$emit('displayItem', this.item.id);
        }
      }
    },
    // 順位がリセットされたら、表示判定もリセットする
    'item.isLoading': {
      handler() {
        if (this.item.isLoading) {
          this.isAlreadyDisplayed = false;
        }
      },
      deep: true
    }
  },
  computed: {
    category() {
      let category = '';

      if (this.categoryLabel[this.item.doneTask.type]) {
        if (this.item.doneTask.isProposal) {
          category += '提案 - ';
        }
        category += this.categoryLabel[this.item.doneTask.type];
      }
      return category;
    },
    categoryCssClass() {
      return {
        'c-list--category__item': true,
        ['c-list--category__item--' + this.item.doneTask.type]: this.item
          .doneTask.type
      };
    },
    categoryLabel() {
      return this.$store.getters['task/solutionLabel'];
    },
    hasDoneTask() {
      return this.item.doneTask != null;
    },
    isChecked() {
      return this.checked.some(check => check.id === this.item.id);
    },
    isClamped() {
      return this.offsetH < this.scrollH;
    },
    isError() {
      const item = this.item;
      const days = this.dates.days;
      // 3日連続してデータ取得中の場合はアラートを出す
      // 登録直後のキーワードは除外
      if (!item.isLoading && !item.isNew) {
        let count = 0;
        const total = days.length;

        for (let i = 0; i < total; i++) {
          const date = days[i].date;
          // GSCのデータが確実にない日（2日前～今日）は除外する
          if (date > CALENDAR.gsc.maxDate) {
            break;
          }

          let isGetted = false;
          // 順位辞書がある場合
          if (item.dictionary) {
            const key = utils.formatDate(date, 'YYYY-MM-DD');
            if (item.dictionary[key] !== undefined) {
              isGetted = true;
            }
          } else if (item.dates) {
            // 順位辞書がない場合はitem.datesの中で日付が同じものを探す
            const time1 = date.getTime();
            const result = item.dates.find(item => {
              const time2 = utils.getDateResetHours(item.date).getTime();
              return time2 === time1;
            });
            if (result) {
              isGetted = true;
            }
          }

          if (!isGetted) {
            count++;
            if (count > 2) {
              return true;
            }
          } else {
            count = 0;
          }
        }
      }
      return false;
    },
    isSelected() {
      return this.item.id === this.selectedId;
    }
  },
  methods: {
    change(checked) {
      this.$emit('check', checked);
    },
    click() {
      this.$refs.checkbox.change();
    },
    goToSupport(e) {
      e.preventDefault();
      window.open(this.$constants.serviceSiteUrl + '/support/');
    },
    select() {
      this.$emit('select', this.item);
    },
    setSize() {
      if (this.$refs.text) {
        this.offsetH = this.$refs.text.offsetHeight;
        this.scrollH = this.$refs.text.scrollHeight;
      } else {
        this.offsetH = 0;
        this.scrollH = 0;
      }
    }
  }
};
</script>
